import { Box, Grid, Modal, Stack, SxProps, Typography } from '@mui/material';
import React, { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IEplassVulnerabilityData } from '../../../../redux/vulnerabilities/sliceVulnerabilities';
import { vars } from '../../../../Theme';
import VulnerabilityStatusTag from './VulnerabilityTags/VulnerabilityStatusTag';
import VulnerabilityCVSSTag from './VulnerabilityTags/VulnerabilityCVSSTag';
import ReactHtmlParser from 'react-html-parser';
import useBreakpoints from '../../../../hooks/useBreakpoint';

interface IDynamicCardProps {
    descHeight: string;
    overflow: string;
    position: string;
    margin?: string;
    cursor?: string;
    bgColor?: string;
    transform?: string;
    top?: string;
    hoverEffect?: unknown;
}

/** Style rules for the vulnerability tile. */
const cardSx: SxProps = {
    border: `1px solid ${vars.lightGray}`,
    borderRadius: 1,
    justifyContent: 'space-between',
    width: '100%',
    px: 1,
    minHeight: '260px',
    outline: 'none',
    maxHeight: `${window.screen.height * 0.7}px`,
    transition: 'all .3s cubic-bezier(0,0,.5,1)',
    boxShadow: '2px 4px 12px rgba(0, 0, 0, .08)',
    inlineSize: '-webkit-fill-available',
};

const hoverEffectSx: SxProps = {
    bgcolor: vars.fillGray,
    cursor: 'pointer',
    transform: 'scale(1.005)',
};

const cardTypeProduct: SxProps = {
    fontSize: '12px',
    color: vars.white,
    borderRadius: '4px',
    p: 0.5,
    marginLeft: 'auto',
};

const typographySx: SxProps = {
    fontSize: 14,
    ml: 1,
    lineHeight: 1.2,
    fontWeight: 'bold',
};

const openVulnerabilitySx = {
    descHeight: '100%',
    overflow: 'auto',
    position: 'absolute',
    margin: '0 32px',
    cursor: 'none',
    bgColor: 'white',
    transform: 'translateY(-50%)',
    top: '50%',
};

/**
 * Component that displays a vulnerability as a tile.
 */
export default (props: IVulnerabilityTileProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'vulnerabilitySelectionView' });

    /** Determine screen size. */
    const { isMobile } = useBreakpoints();

    /** Opens vulnerability dialog. */
    const [openVulnerability, setOpenVulnerability] = useState<boolean>(false);

    const handleOpen = () => setOpenVulnerability(true);
    const handleClose = () => setOpenVulnerability(false);

    /** Gets html string and returns converted string as HTML Box. */
    const HTMLRenderer = (htmlString: string) => ReactHtmlParser(htmlString);

    /** Deconstruct props. */
    const { vulnerability } = props;

    //** Main content from each vulnerability card. */
    const vulnerabilityContent = (cardProps: IDynamicCardProps) => {
        return (
            <Stack
                sx={{
                    ...cardSx,
                    backgroundColor: cardProps.bgColor,
                    position: cardProps.position,
                    top: cardProps.top,
                    transform: cardProps.transform,
                    margin: cardProps.margin,
                    display: isMobile ? 'grid' : 'flex',
                    ':hover': cardProps.hoverEffect,
                }}
                direction='row'
            >
                <Stack sx={{ p: 1, justifyContent: 'space-evenly', display: 'grid' }}>
                    <Stack direction='row' alignItems='center' sx={{ pt: 1, pl: 1 }}>
                        <Box
                            sx={{
                                color: vars.brandBlue,
                                fontSize: 20,
                                flex: 1,
                                fontWeight: 'bold',
                                maxInlineSize: 'fit-content',
                            }}
                            title={vulnerability.ticketId.slice(0, -1)}
                        >
                            {vulnerability.ticketId.slice(0, -1)}
                        </Box>

                        <VulnerabilityStatusTag status={vulnerability.status} />
                    </Stack>

                    <Stack
                        direction='row'
                        sx={{
                            p: 1,
                            maxHeight: cardProps.descHeight,
                            contain: 'paint',
                            overflow: cardProps.overflow,
                        }}
                    >
                        <Typography
                            variant='body1'
                            sx={{ fontSize: 14, ml: 1, lineHeight: 1, fontWeight: 'bold' }}
                            component={'span'}
                        >
                            {HTMLRenderer(vulnerability.description)}
                        </Typography>
                    </Stack>

                    <Stack direction='column' sx={{ p: 1, alignSelf: 'self-end' }}>
                        <Typography
                            variant='body2'
                            sx={{ ...typographySx, fontSize: '12px' }}
                            component={'span'}
                        >
                            {t('published')} {vulnerability.published}
                        </Typography>
                        <Typography variant='body2' sx={{ ...typographySx, fontSize: '12px' }}>
                            {t('updated')} {vulnerability.updated}
                        </Typography>
                    </Stack>
                </Stack>

                <Stack
                    direction='column'
                    sx={{ p: '24px 8px', justifyContent: 'space-evenly', display: 'grid' }}
                >
                    <Stack direction='row' alignItems='center' sx={{ pt: 1, pl: 1 }}>
                        <Typography
                            variant='body2'
                            sx={{ ...typographySx, marginRight: 4 }}
                            component={'span'}
                        >
                            {t('typeOfVulnerability')}
                        </Typography>
                        <Typography
                            variant='body2'
                            sx={{
                                ...typographySx,
                                ...cardTypeProduct,
                                backgroundColor: vars.brandBlue,
                            }}
                            component={'span'}
                        >
                            {vulnerability.type}
                        </Typography>
                    </Stack>

                    <Stack direction='row' alignItems='center' sx={{ pt: 1, pl: 1 }}>
                        <Typography
                            variant='body2'
                            sx={{ ...typographySx, marginRight: 4 }}
                            component={'span'}
                        >
                            {t('affectedProduct')}
                        </Typography>
                        <Typography
                            variant='body2'
                            sx={{
                                ...typographySx,
                                ...cardTypeProduct,
                                backgroundColor: vars.brandBlue,
                            }}
                            component={'span'}
                        >
                            {vulnerability.product}
                        </Typography>
                    </Stack>

                    <Stack direction='row' alignItems='center' sx={{ pt: 1, pl: 1 }}>
                        <Typography
                            variant='body2'
                            sx={{ ...typographySx, marginRight: 4 }}
                            component={'span'}
                        >
                            {t('vulnerability_severity')}
                        </Typography>
                        <VulnerabilityStatusTag
                            status={vulnerability.severity}
                            style={{ marginLeft: 'auto' }}
                        />
                    </Stack>

                    <Stack direction='row' alignItems='center' sx={{ pt: 1, pl: 1 }}>
                        <Typography
                            variant='body2'
                            sx={{ ...typographySx, marginRight: 4 }}
                            component={'span'}
                        >
                            {t('vulnerability_cvss')}
                        </Typography>
                        <VulnerabilityCVSSTag
                            cvssScore={vulnerability.cvss}
                            style={{ marginLeft: 'auto' }}
                        />
                    </Stack>

                    <Stack direction='row' alignItems='center' sx={{ pt: 1, pl: 1 }}>
                        <Typography
                            variant='body2'
                            sx={{ ...typographySx, marginRight: 4 }}
                            component={'span'}
                        >
                            {t('vulnerability_cvss_identifier')}
                        </Typography>
                        <Typography
                            variant='body2'
                            sx={{ ...typographySx, ...cardTypeProduct, color: 'black' }}
                            component={'span'}
                        >
                            {vulnerability.cvss_identifier}
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>
        );
    };

    return (
        <>
            <Grid
                item
                xs={12 / props.size}
                style={{ ...props.style, display: 'contents' }}
                onClick={handleOpen}
            >
                {vulnerabilityContent({
                    descHeight: '161px',
                    overflow: 'hidden',
                    position: 'static',
                    hoverEffect: hoverEffectSx,
                })}
            </Grid>

            <Modal
                sx={{ backdropFilter: 'blur(6px)' }}
                open={openVulnerability}
                onClose={handleClose}
            >
                {vulnerabilityContent(openVulnerabilitySx)}
            </Modal>
        </>
    );
};

/**
 * Interface that describes the properties needed for VulnerabilityTile.
 */
export interface IVulnerabilityTileProps {
    /** Eplass vulnerability Object. */
    vulnerability: IEplassVulnerabilityData;

    /** Tile grid size. */
    size: number;

    /** (Optional) Additional CSS styles. */
    style?: CSSProperties;
}
